import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Copy } from 'lucide-react';
import { useState, type ReactElement, type ReactNode } from 'react';

import { AccessDenied, Badge, ErrorState, Icon, LoadingState } from '@/ui/kit';
import { fontStacks } from '@/ui/tokens';

import type { AccountState } from './accountPresentation';
import type { AdminAccess } from './useAdminAccess';

/**
 * Briques partagees par la liste et la fiche.
 *
 * Elles vivent a part pour une raison simple : les deux ecrans doivent dire la
 * **meme chose** de la meme situation. Un compte anonymise annonce dans la liste
 * ce qu'il annonce dans sa fiche, et un role insuffisant produit exactement la
 * meme explication des deux cotes — sans quoi l'exploitant croirait a deux
 * problemes differents.
 *
 * Depuis le kit (`ui/kit/`), il ne reste ici que ce qui est **propre aux
 * comptes** : la traduction des etats d'un compte en pastilles, l'identifiant
 * copiable et la traduction de l'acces d'administration en bloc d'etat. Le
 * chargement, l'erreur et le refus d'acces eux-memes sont ceux du kit, communs
 * a tous les ecrans.
 */

/** Les tons d'etat d'un compte ont tous un ton de pastille du meme nom. */
export const StateChips = ({ states }: { states: readonly AccountState[] }): ReactElement => (
  <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
    {states.map((state) => (
      <Badge key={state.label} label={state.label} tone={state.tone} />
    ))}
  </Stack>
);

/**
 * Identifiant technique, au second rang et copiable.
 *
 * Regle de la cible : **aucun identifiant technique en valeur principale**. Un
 * UUID ne se lit pas et ne se retient pas ; il se copie. Il reste neanmoins
 * affiche, parce que c'est lui qu'on colle dans une commande console ou dans un
 * echange avec un developpeur.
 *
 * La copie est **facultative** : `navigator.clipboard` n'existe pas dans tous
 * les contextes (page non securisee, navigateur restreint). Son echec ne doit
 * rien casser — le texte reste selectionnable a la main.
 */
export const TechnicalId = ({
  value,
  label,
  abbreviated = false,
}: {
  value: string;
  label: string;
  /** Liste : « 01a0fb47…64cdf » ; la copie et l'infobulle portent toujours l'identifiant entier. */
  abbreviated?: boolean;
}): ReactElement => {
  const [copied, setCopied] = useState(false);

  const copy = (): void => {
    // `try/catch` autour de l'ACCES, pas seulement de l'appel : la
    // specification declare `navigator.clipboard` toujours present — et les
    // typages avec elle —, la realite non (page servie en clair, navigateur
    // restreint, jsdom sans double). Lire la propriete leve alors, et une copie
    // impossible ne doit rien casser : le texte reste selectionnable a la main.
    try {
      void navigator.clipboard
        .writeText(value)
        .then(() => {
          setCopied(true);
        })
        .catch(() => {
          setCopied(false);
        });
    } catch {
      setCopied(false);
    }
  };

  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
      <Typography
        variant="caption"
        color="text.secondary"
        title={abbreviated ? value : undefined}
        sx={{ fontFamily: fontStacks.mono, wordBreak: 'break-all' }}
      >
        {abbreviated && value.length > 16 ? `${value.slice(0, 8)}…${value.slice(-7)}` : value}
      </Typography>
      <Tooltip title={copied ? 'Copié' : `Copier ${label}`}>
        <IconButton size="small" aria-label={`Copier ${label}`} onClick={copy}>
          <Icon icon={Copy} size={14} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

/**
 * Une ligne « libelle : valeur » de la fiche et de ses cartes.
 *
 * Une liste de definitions (`<dt>`/`<dd>`) et non deux `<span>` : un lecteur
 * d'ecran annonce alors le libelle avec sa valeur. Le libelle prend la ligne
 * entiere sous 600 px, ou le volet de la fiche est etroit : une colonne fixe de
 * 200 px n'y laisserait rien a la valeur.
 */
export const Fact = ({ label, children }: { label: string; children: ReactNode }): ReactElement => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px', justifyContent: 'space-between', py: 0.75 }}>
    <Typography component="dt" sx={{ fontSize: 13, color: 'tokens.textMuted' }}>
      {label}
    </Typography>
    <Typography component="dd" sx={{ m: 0, fontSize: 13, fontWeight: 700, textAlign: 'right' }}>
      {children}
    </Typography>
  </Box>
);

/** Conteneur des `Fact` : filets entre les lignes, sans marge propre. */
export const Facts = ({ children }: { children: ReactNode }): ReactElement => (
  <Box
    component="dl"
    sx={{ m: 0, '& > div + div': { borderTop: 1, borderColor: 'tokens.border' } }}
  >
    {children}
  </Box>
);

/**
 * Ce qu'on affiche tant qu'on ne sait pas, ou qu'on n'a pas le droit.
 *
 * Le cas `denied` est le plus important, et c'est le plus frequent : **tout
 * compte parent est dans cet etat** (ADR 0032, `admin_role` nul par defaut). Il
 * n'est donc ni une panne ni une faute, et surtout il ne deconnecte personne —
 * l'ecran dit ce qui manque et comment cela s'obtient.
 */
export const AccessPanel = ({
  access,
  onRetry,
}: {
  access: AdminAccess;
  onRetry: () => void;
}): ReactElement | null => {
  if (access.kind === 'granted') {
    return null;
  }

  if (access.kind === 'loading') {
    return <LoadingState label="Vérification de votre accès à l’administration…" />;
  }

  if (access.kind === 'error') {
    return <ErrorState message={access.message} onRetry={onRetry} />;
  }

  return (
    <AccessDenied title="Votre compte n’a pas accès à l’administration">
      Vous êtes bien connecté, mais ce compte n’a aucun rôle d’administration : il ne peut donc pas
      consulter les comptes des autres personnes. Un rôle s’attribue depuis le serveur, jamais
      depuis cet écran.
    </AccessDenied>
  );
};
